import cn from 'classnames';
import React, { memo, useCallback, useMemo } from 'react';

import MenuActive from 'assets/icon/showmore-active-small.svg';
import Menu from 'assets/icon/showmore-small.svg';
import { Popover } from 'components/atoms/popover';
import Text from 'components/atoms/text';
import useTableMenu from 'components/molecules/tableMenu/useTableMenu';
import { MenuItem, TableInstances } from 'types/table';

import { TEXTS } from './config';
import sm from './styles.module.scss';
import { Props, TableMenuComponent, UseTableMenu } from './types';

const TableMenu = <T extends TableInstances>({
  onClick,
  data,
  menuConfig,
  withRightMargin,
  renderAsTooltip,
}: Props<T>) => {
  // region *******************************DATA*********************************
  const {
    handleOpenMenu,
    menuItemClickHandler,
    menuWithVisibleItems,
    menuRef,
    isMenuOpened,
  }: UseTableMenu<T> = useTableMenu(
    useMemo(
      () => ({ onClick, menuConfig, data, renderAsTooltip }),
      [data, menuConfig, onClick, renderAsTooltip]
    )
  );
  // endregion

  // region ********************************JSX*********************************
  const renderItem: (item: MenuItem<T>) => JSX.Element = useCallback(
    (item) => {
      const isItemDisabled = item.isDisabled ? item.isDisabled(data) : false;

      return (
        <div
          className={cn(sm.Menu_Item, {
            [sm.Menu_Item_Disabled]: isItemDisabled,
          })}
          key={`${item.accessor}-${new Date().getTime()}`}
          role="button"
          onClick={menuItemClickHandler(item.accessor, isItemDisabled)}
        >
          <Text
            content={item.label}
            withRedColor={
              typeof item.isDangerAction === 'function'
                ? item.isDangerAction(data)
                : item.isDangerAction
            }
            withGreyColor={isItemDisabled}
          />
        </div>
      );
    },
    [data, menuItemClickHandler]
  );

  const MenuButton = (
    <div
      role="button"
      onClick={handleOpenMenu}
      className={cn([
        sm.Btn,
        {
          [sm.Btn_WithRightMargin]: withRightMargin,
        },
      ])}
    >
      <img src={isMenuOpened ? MenuActive : Menu} alt={TEXTS.ALT.MENU} />
    </div>
  );

  return menuWithVisibleItems?.length ? (
    <div className={sm.Wrap} ref={menuRef}>
      {(!isMenuOpened || !renderAsTooltip) && MenuButton}
      {isMenuOpened && (
        <>
          {renderAsTooltip ? (
            <>
              <Popover
                visible
                popoverChildren={<>{menuWithVisibleItems?.map(renderItem)}</>}
                classes={sm.Tooltip}
              >
                {MenuButton}
              </Popover>
            </>
          ) : (
            <div className={sm.Menu}>
              {menuWithVisibleItems?.map(renderItem)}
            </div>
          )}
        </>
      )}
    </div>
  ) : null;
  // endregion
};

export default memo(TableMenu) as TableMenuComponent;
